import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  /** 저장된 세션을 처음 읽어오는 중. 이 동안은 로그인 여부를 모르므로 리다이렉트하면 안 된다. */
  loading: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * 로그인 상태를 앱 전체에 알린다. 서버가 쿠키로 세션을 읽던 구조(proxy.ts)를 대신해, supabase-js가
 * localStorage 세션을 읽고(onAuthStateChange) 토큰 갱신·로그아웃을 이벤트로 알려준다.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });
    return () => subscription.subscription.unsubscribe();
  }, []);

  const value = useMemo(() => ({ user: session?.user ?? null, session, loading }), [session, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth는 AuthProvider 안에서만 쓸 수 있습니다.");
  return ctx;
}
